// 全屏 on a phone (artin 11-02: 「手机版应该主动要求全屏」): the browser's address bar and toolbars take a good part of a
// phone's screen, so Deck asks to go full screen there. What a browser allows:
//  - Android (Chrome, Edge, Samsung...), an iPad: the Fullscreen API, only inside a user gesture. 上船 is one (a tap, or
//    Enter): the page goes full screen there. If the browser leaves full screen (back, another app), the next tap on
//    the page asks again, unless I turned it off: the 「退出全屏」 pill in the top bar, or 设置 → 全屏 (kept per device).
//    In OtterBonk (public/otterbonk/client.js) the game handles full screen itself (and turns the phone to landscape).
//  - an iPhone: no Fullscreen API for pages (Safari only does videos). Only a home-screen app (分享 → 添加到主屏幕,
//    display: standalone in the manifest) hides Safari's bars, so a one-time card says how, and 设置 → 全屏 shows it again.
//  - already the installed app (display-mode standalone), or no touch screen: nothing to do, nothing shown.
// app.js calls initFullscreen() once the pre-join page is up (it watches for 上船, #go).
import { icon } from '/icons.js';
import { t, tr, onLang } from '/i18n.js';

const PREF = 'deck.fullscreen'; // '0': don't ask (off); anything else: ask (the default)
const HINT = 'deck.fsHint'; // '1': the iPhone card was shown once
const de = document.documentElement;
const fsEl = () => document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
/** A touch screen, told the way deck/stick.js and OtterBonk tell one. */
const touch = matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints > 0 && !matchMedia('(hover: hover)').matches);
const iphone = /iPhone|iPod/.test(navigator.userAgent);
// the installed app (told before any full screen of ours: Chrome also matches display-mode fullscreen while a page is)
const installed = matchMedia('(display-mode: standalone), (display-mode: window-controls-overlay)').matches || navigator.standalone === true
  || (matchMedia('(display-mode: fullscreen)').matches && !fsEl());
const req = de.requestFullscreen ?? de.webkitRequestFullscreen;
const exit = document.exitFullscreen ?? document.webkitExitFullscreen;
const apiOk = !iphone && !!req && (document.fullscreenEnabled ?? document.webkitFullscreenEnabled) !== false;
/** 'api': ask for full screen; 'ios': say how to add Deck to the home screen; null: nothing. */
export const fullscreenMode = !touch || installed ? null : apiOk ? 'api' : iphone ? 'ios' : null;

const CSS = `#fsBtn{box-sizing:border-box;height:30px;white-space:nowrap;background:#fff8e7;border:2px solid #1b1410;border-radius:10px;padding:4px 10px;font:12px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;cursor:pointer}
#fsCard{position:fixed;z-index:75;left:50%;transform:translateX(-50%);bottom:calc(var(--barh, calc(76px + var(--sb, 0px))) + 12px);width:min(340px,calc(100vw - 24px - var(--sl, 0px) - var(--sr, 0px)));max-height:calc(100vh - var(--barh, 76px) - 24px - var(--st, 0px));max-height:calc(100dvh - var(--barh, 76px) - 24px - var(--st, 0px));overflow:auto;box-sizing:border-box;background:#fff8e7;border:3px solid #1b1410;border-radius:16px;padding:12px 14px;box-shadow:0 4px 0 #1b1410;font:13px/1.55 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410}
#fsCard b{display:block;font-size:15px;margin-bottom:4px}
#fsCard p{margin:0 0 6px}
#fsCard ol{margin:4px 0 8px 18px;padding:0}
#fsCard li{margin:2px 0}
#fsCard .fsx{position:absolute;right:8px;top:8px;border:2px solid #1b1410;border-radius:8px;background:#fff;padding:1px 3px;line-height:0;cursor:pointer}
#fsCard .fsok{display:block;width:100%;font:inherit;font-weight:600;border:3px solid #1b1410;border-radius:999px;padding:6px;background:#FDD03B;box-shadow:0 3px 0 #1b1410;cursor:pointer}
#fsCard .fsnote{font-size:12px;color:#555;margin:6px 0 0}
#settings .fsrow{display:flex;gap:6px;align-items:center;font-size:13px;margin:4px 0;cursor:pointer}
#settings .fsnote2{font-size:12px;color:#666}
#settings .fsgo{font:inherit;border:2px solid #1b1410;border-radius:999px;padding:3px 14px;background:#FDD03B;cursor:pointer}`;

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
/** el() whose text is a catalog key, kept in the current language (tr). */
const elk = (tag, cls, key) => tr(el(tag, cls), key);
/** The top bar's toggle: full screen now or not -> its text. */
const FS_BTN = { on: 'pwa.fullscreen.btn.on', off: 'pwa.fullscreen.btn.off' };

/** The iPhone card: why and how to add Deck to the home screen. Shown once by itself; 设置 → 全屏 opens it again. */
function showCard() {
  document.getElementById('fsCard')?.remove();
  try { localStorage.setItem(HINT, '1'); localStorage.setItem('deck.installHint', '1'); } catch {} // (and public/pwa.js's own pill is not needed)
  const c = el('div'); c.id = 'fsCard'; c.setAttribute('role', 'dialog'); tr(c, null, null, { 'aria-label': 'pwa.fullscreen.card.title' });
  const x = el('button', 'fsx'); x.type = 'button'; x.append(icon('close')); tr(x, null, null, { 'aria-label': 'pwa.fullscreen.close' });
  const ol = el('ol');
  const steps = [['pwa.fullscreen.card.step1', 'upload'], ['pwa.fullscreen.card.step2'], ['pwa.fullscreen.card.step3']];
  for (const [k, i] of steps) { const li = el('li'); li.append(elk('span', '', k)); if (i) li.append(icon(i)); ol.append(li); }
  const ok = elk('button', 'fsok', 'pwa.fullscreen.card.ok'); ok.type = 'button';
  c.append(x, elk('b', '', 'pwa.fullscreen.card.title'), elk('p', '', 'pwa.fullscreen.card.why'),
    ol, ok, elk('p', 'fsnote', 'pwa.fullscreen.card.note'));
  x.onclick = ok.onclick = () => c.remove();
  document.body.append(c);
}

export function initFullscreen({ addSettingsSection }) {
  const mode = fullscreenMode;
  if (!mode) return;
  const st = document.createElement('style'); st.textContent = CSS; document.head.append(st);
  let boarded = false;

  if (mode === 'ios') {
    addSettingsSection({ id: 'fullscreen', title: () => t('pwa.fullscreen.title'), icon: 'screen', order: 15, render(box) {
      box.append(el('p', '', t('pwa.fullscreen.ios.about')));
      const b = el('button', 'fsgo', t('pwa.fullscreen.ios.how')); b.type = 'button'; b.onclick = () => { const g = document.getElementById('settingsBtn'); if (g?.getAttribute('aria-expanded') === 'true') g.click(); showCard(); }; // (设置 closes: the card is under it)
      box.append(b);
    } });
    // once, a few seconds after boarding (the map is up and the tour, if any, has its turn first)
    const later = (ms = 8000) => setTimeout(() => {
      let seen = false; try { seen = !!localStorage.getItem(HINT); } catch {}
      if (seen || document.querySelector('.ob')) return; // (not over OtterBonk: its own card says what an iPhone can do)
      const t = document.getElementById('tour'); if (t && t.style.display !== 'none') return later(4000); // after the tour
      showCard();
    }, ms);
    document.addEventListener('click', (e) => { if (!boarded && e.target.closest?.('#go')) { boarded = true; later(); } }, true);
    return;
  }

  // ---- 'api': Android and the like
  // read again where it matters: OtterBonk's own 全屏 / 退出全屏 writes it too (public/otterbonk/client.js)
  const read = () => { try { return localStorage.getItem(PREF) !== '0'; } catch { return on; } };
  let on = true; on = read();
  const save = () => { try { localStorage.setItem(PREF, on ? '1' : '0'); } catch {} };
  let pending = false;
  function enter() {
    if (fsEl() || pending) return;
    pending = true;
    // (turned off while the request was on its way, e.g. the tap on 设置 → 全屏's own checkbox: leave again)
    try { Promise.resolve(req.call(de, { navigationUI: 'hide' })).catch(() => {}).finally(() => { pending = false; if (!on) leave(); }); } catch { pending = false; }
  }
  const leave = () => { if (fsEl()) try { Promise.resolve(exit?.call(document)).catch(() => {}); } catch {} };

  // the toggle in the top bar (after boarding): 全屏 / 退出全屏; pressing it also sets the preference
  const btn = el('button'); btn.id = 'fsBtn'; btn.type = 'button'; btn.style.display = 'none';
  const render = () => { btn.textContent = t(FS_BTN[fsEl() ? 'on' : 'off']); btn.setAttribute('aria-pressed', String(!!fsEl())); };
  btn.onclick = () => { if (fsEl()) { on = false; save(); leave(); } else { on = true; save(); enter(); } };
  document.addEventListener('fullscreenchange', render); document.addEventListener('webkitfullscreenchange', render);
  onLang(render);
  render();

  addSettingsSection({ id: 'fullscreen', title: () => t('pwa.fullscreen.title'), icon: 'screen', order: 15, render(box) {
    on = read(); const l = el('label', 'fsrow'); const cb = el('input'); cb.type = 'checkbox'; cb.checked = on;
    cb.onchange = () => { on = cb.checked; save(); if (on) enter(); else leave(); }; // (a change is a user gesture)
    l.append(cb, document.createTextNode(t('pwa.fullscreen.auto')));
    box.append(l, el('p', 'fsnote2', t('pwa.fullscreen.autoNote')));
  } });

  // 上船 (a tap or Enter: a user gesture) goes full screen; after that, a tap while not full screen asks again. A touch's
  // pointerdown isn't a gesture for the Fullscreen API, its pointerup is (and a click). Inside OtterBonk the game asks.
  const gesture = (e) => {
    if (!boarded) {
      if (e.type !== 'click' || !e.target.closest?.('#go')) return;
      boarded = true; btn.style.display = '';
      (document.getElementById('topleft') || document.body).append(btn);
    }
    if (e.type === 'pointerup' && e.pointerType === 'mouse') return;
    on = read();
    // not on the pill or in 设置 (they say what they want themselves), nor a text field (an iPad's keyboard and full
    // screen don't mix), nor in OtterBonk
    if (!on || fsEl() || e.target === btn || e.target.closest?.('#settings, input, textarea, [contenteditable]') || document.querySelector('.ob')) return;
    enter();
  };
  addEventListener('pointerup', gesture, true); addEventListener('click', gesture, true);
}
